import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getAllAuctionUsingPaging } from "../api/ApiRoutesFile";

export const auctionApiSlice = createApi({
  reducerPath: "auctionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Auctions"],
  endpoints: (builder) => ({
    auctions: builder.query({
      query: ({ endpoint, id, search, status, filters = {} }) => {
        const url = id ? `${endpoint}${id}` : endpoint;
        
        // Build query parameters
        const params = {};
        
        // Add search parameter if provided
        if (search) params.search = search;
        
        // Handle status filter
        if (status && status !== "all") {
          params.status = status;
        }

        // Additional server-side filters for auction management
        if (filters.type) params.type = filters.type;
        if (filters.category) params.category = filters.category;
        if (filters.subcategory) params.subcategory = filters.subcategory;
        if (filters.fromDate) params.fromDate = filters.fromDate;
        if (filters.toDate) params.toDate = filters.toDate;
        if (filters.sortBy) params.sortBy = filters.sortBy;
        if (filters.sortOrder) params.sortOrder = filters.sortOrder;
        
        return {
          url,
          method: "GET",
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
      providesTags: (result, error, arg) => {
        // Include the search and status in the tag to properly invalidate with parameters
        const baseTag = {
          type: "Auctions",
          id: `LIST:${arg.search || ""}:${arg.status || ""}:${JSON.stringify(arg.filters || {})}`,
        };
        
        if (!result?.auctions) return [baseTag];
        
        return [
          baseTag,
          ...result.auctions.map(({ _id }) => ({ type: "Auctions", id: _id })),
        ];
      }
    }),

    createAuction: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [{ type: "Auctions", id: "LIST" }],
      async onQueryStarted(
        { endpoint, formData, lastId, search = "", status = "" },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          if (data && data.auction && data.auction._id) {
            dispatch(
              auctionApiSlice.util.updateQueryData(
                "auctions",
                {
                  endpoint: getAllAuctionUsingPaging,
                  id: lastId,
                  search,
                  status,
                },
                (draft) => {
                  if (draft && draft.auctions) {
                    draft.auctions.unshift(data.auction);
                    if (draft.count && draft.count.totalPage) {
                      draft.count.totalPage += 1;
                    }
                  }
                }
              )
            );
          }
        } catch (error) {
          // Optionally handle error
        }
      },
    }),

    updateAuction: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Auctions", id },
        { type: "Auctions", id: "LIST" }
      ]
    }),

    deleteAuction: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Auctions", id },
        { type: "Auctions", id: "LIST" }
      ]
    }),
  }),
});

export const {
  useAuctionsQuery,
  useCreateAuctionMutation,
  useUpdateAuctionMutation,
  useDeleteAuctionMutation,
} = auctionApiSlice;

